import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { replace } from 'redux-router';
import {
  exceptionTypes,
  fetchUserInfoDeferred,
  IExceptionAction,
  logException,
  LogException,
  UserEntersApartmentNumber,
  userEntersApartmentNumber,
  userEntersCity,
  UserEntersCity,
  UserEntersDeliveryPointId,
  userEntersDeliveryPointId,
  userEntersEntrance,
  UserEntersEntrance,
  userEntersFloor,
  UserEntersFloor,
  userEntersHouseNumber,
  UserEntersHouseNumber,
  UserEntersStreetAddress,
  userEntersStreetAddress,
  userEntersZipCode,
  UserEntersZipCode
} from '../actions/Actions';
import { IAddress, IQuery, IState as IAppState, Replace } from '../CustomTypes';
import { padWithZerosLengthFour } from '../fastflowUtils';
import { validateCity, validateZipCode, zipDeliverable } from '../validators';
import './Address.scss';

export interface IDispatchProps {
  userEntersCity: UserEntersCity;
  userEntersStreetAddress: UserEntersStreetAddress;
  userEntersHouseNumber: UserEntersHouseNumber;
  userEntersEntrance: UserEntersEntrance;
  userEntersZipCode: UserEntersZipCode;
  userEntersFloor: UserEntersFloor;
  userEntersApartmentNumber: UserEntersApartmentNumber;
  userEntersDeliveryPointId: UserEntersDeliveryPointId;
  replace: Replace;
  logException: LogException;
}
export interface IParamProps {
  formSubmitted?: boolean;
  validZipCodes?: number[];
}
export interface IStateProps {
  address?: IAddress;
  pathname?: string;
  deliveryPointId?: string;
  query?: IQuery;
  exceptions?: IExceptionAction[];
}
export interface IProps extends IParamProps, IDispatchProps, IStateProps {}
export interface IState {
  showFloorAndHousehold: boolean;
  showZipError: boolean;
}
export class Address extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.initializeDiAddress = this.initializeDiAddress.bind(this);
    this.state = {
      showFloorAndHousehold: false,
      showZipError: false
    };
  }

  public componentDidMount() {
    const logException = this.props.logException;
    fetchUserInfoDeferred.promise
      .then(() => {
        this.initializeDiAddress();
      })
      .catch(e => {
        logException(exceptionTypes.EXCEPTION, e);
        this.initializeDiAddress();
      });
  }

  public render() {
    const {
      userEntersStreetAddress,
      userEntersHouseNumber,
      userEntersFloor,
      userEntersApartmentNumber,
      userEntersZipCode,
      userEntersCity
    } = this.props;
    const hideFloorAndHouseholdClass = this.state.showFloorAndHousehold
      ? ''
      : 'hide';

    const { zipCode, city } = this.props.address;
    const isValid =
      validateZipCode(zipCode, this.props.validZipCodes) && validateCity(city);
    const showZipError = !zipDeliverable(zipCode, this.props.validZipCodes);
    const showValidationError =
      this.props.formSubmitted && !isValid && !showZipError;

    return (
      <div
        id="di-provide-address"
        className="address ah-default-skin col-xs-12"
      >
        <input type="hidden" className="selectionCache" />

        <div className="col-xs-6 inputField form-group">
          <input
            type="text"
            className="streetInput form-control"
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersStreetAddress(e.target.value)}
          />
          <label>Gate/vei</label>
          <div className="streetContainer" />
        </div>
        <div className="col-xs-6 inputField form-group">
          <input
            type="text"
            className="streetNumberInput form-control"
            maxLength={5}
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersHouseNumber(e.target.value)}
          />
          <label>Gatenr.</label>
          <div className="streetNumberContainer" />
        </div>

        <div
          className={
            'col-xs-6 inputField form-group ' + hideFloorAndHouseholdClass
          }
        >
          <input
            type="text"
            className="floorInput form-control"
            maxLength={16}
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersFloor(e.target.value)}
          />
          <label>Etasje</label>
          <div className="floorContainer" />
        </div>
        <div
          className={
            'col-xs-6 inputField form-group ' + hideFloorAndHouseholdClass
          }
        >
          <input
            type="text"
            className="householdInput form-control"
            maxLength={8}
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersApartmentNumber(e.target.value)}
          />
          <label>Leilighet</label>
          <div className="householdContainer" />
        </div>

        <div className="col-xs-6 inputField form-group">
          <input
            type="tel"
            className="zipLabel form-control"
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersZipCode(e.target.value)}
          />
          <label>Postnr.</label>
          <div className="zipContainer" />
        </div>
        <div className="col-xs-6 inputField form-group">
          <input
            type="text"
            className="cityLabel form-control"
            autoComplete="false"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={e => userEntersCity(e.target.value)}
          />
          <label>Poststed</label>
          <div className="cityContainer" />
        </div>

        {showValidationError && (
          <div className="col-xs-12 validation-messages">
            <div className="alert alert-error">
              Vennligst oppgi både postnummer og poststed
            </div>
          </div>
        )}
        {showZipError && (
          <div className="col-xs-12 validation-messages">
            <div className="alert alert-error">
              Beklager. Vi sender dessverre ikke avisen til denne adressen enda.
              Ta kontakt med kundeservice om du trenger hjelp med abonnement.
            </div>
          </div>
        )}
      </div>
    );
  }

  /*global DI*/
  private initializeDiAddress() {
    if ((window as any).DI) {
      const addressHelper = new DI.AddressHelper('di-provide-address', {
        allowFreeform: true,
        apiKey: 'qJuSiqn+5yT2A4ADZBSWIMu46At9Q+oP',
        limitToOfficial: false,
        useGeoLocation: true
      });

      const { deliveryPointId, replace, address } = this.props;
      if (deliveryPointId && deliveryPointId !== '999999999999') {
        addressHelper.initializeFromDeliverypointId(deliveryPointId);
      } else {
        const {
          streetAddress,
          houseNumber,
          entrance,
          zipCode,
          city,
          floor,
          apartmentNumber
        } = address;
        if (streetAddress && streetAddress.length > 0) {
          addressHelper.parseAddress(
            streetAddress,
            houseNumber,
            entrance,
            zipCode,
            city,
            null,
            floor,
            apartmentNumber
          );
        }
      }

      addressHelper.selectionChangeEvent.subscribe(() => {
        const address = addressHelper.getSelectedAddress();
        const complete = addressHelper.isComplete();
        const needsFloor =
          !(complete && address.floor === -1) &&
          address.streetNumber &&
          !address.streetNumber.ignoreHouseholds();
        const needsHousehold =
          needsFloor && address.floor && address.household !== -1;
        const mergedAddress = Object.assign({}, address, {
          complete,
          needsFloor,
          needsHousehold
        });

        this.setState({
          showFloorAndHousehold: needsFloor || needsHousehold
        });

        if (mergedAddress.street && mergedAddress.street !== -1) {
          this.props.userEntersStreetAddress(mergedAddress.street.streetName);
          this.props.userEntersCity(mergedAddress.street.city);
        }
        if (mergedAddress.streetNumber) {
          this.props.userEntersHouseNumber(
            '' + mergedAddress.streetNumber.streetNo
          );
          this.props.userEntersEntrance(
            '' + mergedAddress.streetNumber.entrance
          );
          this.props.userEntersZipCode(
            padWithZerosLengthFour('' + mergedAddress.streetNumber.postalCode)
          );
        }
        if (address.floor && address.floor !== -1) {
          this.props.userEntersFloor('' + address.floor.floorNo);
        }
        if (address.household && address.household !== -1) {
          this.props.userEntersApartmentNumber(
            '' + address.household.flatNo || ''
          );
        }

        const household = mergedAddress.household;
        const streetNumber = mergedAddress.streetNumber;
        const deliveryPointId =
          (household && household !== -1 && household.deliveryPointId) ||
          (streetNumber && streetNumber.deliveryPointId);

        this.props.userEntersDeliveryPointId(
          (deliveryPointId && '' + deliveryPointId) ||
            (streetNumber && '' + streetNumber.deliveryPointId) ||
            ''
        );

        if (mergedAddress.complete) {
          if (deliveryPointId) {
            replace({
              pathname: this.props.pathname,
              query: Object.assign({}, this.props.query, {
                city: mergedAddress.street !== -1 && mergedAddress.street.city,
                countryCode:
                  address.street !== -1 && address.street.countryCode,
                deliveryPointId,
                entrance: mergedAddress.streetNumber.entrance,
                flat:
                  (address.household &&
                    address.household !== -1 &&
                    address.household.flatNo) ||
                  '',
                floor: address.floor !== -1 && address.floor.floorNo,
                floortype: address.floor !== -1 && address.floor.floorType,
                streetname:
                  mergedAddress.street !== -1 &&
                  mergedAddress.street.streetName,
                streetno: mergedAddress.streetNumber.streetNo,
                zip: mergedAddress.streetNumber.postalCode
              })
            });
          }
        }
      });
    }
  }
}

export default connect<IStateProps, IDispatchProps, {}>(
  (state: IAppState) => ({
    address: state.address,
    deliveryPointId: state.router.location.query.deliveryPointId,
    exceptions: state.exceptions,
    pathname: state.router.location.pathname,
    query: state.router.location.query
  }),
  dispatch =>
    bindActionCreators(
      {
        logException,
        replace,
        userEntersApartmentNumber,
        userEntersCity,
        userEntersDeliveryPointId,
        userEntersEntrance,
        userEntersFloor,
        userEntersHouseNumber,
        userEntersStreetAddress,
        userEntersZipCode
      },
      dispatch
    )
)(Address);



// WEBPACK FOOTER //
// ./src/input-components/Address.tsx